import type { User } from './user';

export type VariableType = 'single_select' | string;

/** Cohort variables are text-only for now; sent explicitly on create (API locks type once values exist). */
export const COHORT_VARIABLE_TYPE = 'text' as const;

export interface VariableProps {
  options?: string[];
  [key: string]: any;
}

export interface Variable {
  id: number;
  name: string;
  description: string;
  type: VariableType;
  type_display?: string;
  props: VariableProps;
  visible: boolean;
  required: boolean;
  nullable: boolean;
  value: any;
  current_value?: any;
  sequence?: number;
  values?: CohortVariableValue[];
  created_by?: User;
  created_at?: string;
}

export interface CohortVariableValue {
  cohort_id: number;
  value: string | boolean | number | string[] | null;
}

export interface CreateCohortVariableDefinition {
  name: string;
  description?: string;
  type: VariableType;
  props: VariableProps & { source: 'cohort' };
  required?: boolean;
  nullable?: boolean;
  visible?: boolean;
  values?: CohortVariableValue[];
}

export interface CreateCohortVariablePayload {
  content_type: 'orgs.org';
  variables: CreateCohortVariableDefinition | CreateCohortVariableDefinition[];
}

export interface UpdateCohortVariablePayload {
  name?: string;
  description?: string;
  type?: VariableType;
  props?: VariableProps;
  required?: boolean;
  nullable?: boolean;
  visible?: boolean;
  values?: CohortVariableValue[];
}

export function isCohortVariable(
  variable: Pick<Variable, 'props'>
): boolean {
  return variable.props?.source === 'cohort';
}

export function buildCohortValueMap(
  values: CohortVariableValue[] | undefined
): Map<number, CohortVariableValue['value']> {
  const map = new Map<number, CohortVariableValue['value']>();
  values?.forEach((entry) => {
    map.set(entry.cohort_id, entry.value);
  });
  return map;
}

export function formatCohortVariableValue(
  value: CohortVariableValue['value']
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

export interface CreateVariablePayload {
  content_type: string;
  variables: Omit<Variable, 'id' | 'value'>[];
}

export interface UpdateVariablePayload {
  [key: string]: any;
}

export type MyVariable = Omit<Variable, 'type_display' | 'current_value'>;

export type UserVariableUpdateValue = string | boolean | string[] | number | null;

export type UserVariableUpdatePayload = Record<string, UserVariableUpdateValue>;

export function getProfileVariableLabel(variable: {
  name: string;
  description?: string;
}): string {
  return (
    variable.description ||
    variable.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
}

export function isNumericProfileVariableType(type: string): boolean {
  return type === 'integer' || type === 'float';
}

function getRequiredFieldError(
  label: string,
  trimmed: string,
  required: boolean
): string | undefined {
  if (required && !trimmed) {
    return `${label} is required`;
  }

  return undefined;
}

function getIntegerFieldError(label: string, trimmed: string): string | undefined {
  if (!/^-?\d+$/.test(trimmed)) {
    return `${label} must be a whole number`;
  }

  return undefined;
}

function getFloatFieldError(label: string, trimmed: string): string | undefined {
  if (!/^-?(?:\d+\.\d+|\d+|\.\d+)$/.test(trimmed)) {
    return `${label} must be a valid number`;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return `${label} must be a valid number`;
  }

  return undefined;
}

export function getProfileVariableFieldError(
  variable: Pick<MyVariable, 'name' | 'description' | 'type' | 'required' | 'nullable'>,
  value: string
): string | undefined {
  const label = getProfileVariableLabel(variable);
  const trimmed = value.trim();

  if (variable.type === 'boolean') {
    return undefined;
  }

  const requiredError = getRequiredFieldError(label, trimmed, variable.required);
  if (requiredError) {
    return requiredError;
  }

  if (!trimmed) {
    return undefined;
  }

  if (variable.type === 'integer') {
    return getIntegerFieldError(label, trimmed);
  }

  if (variable.type === 'float') {
    return getFloatFieldError(label, trimmed);
  }

  return undefined;
}

function buildNumericPayloadValue(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed ? Number(trimmed) : null;
}

export function buildUserVariableUpdatePayload(
  variables: Pick<MyVariable, 'name' | 'type' | 'nullable'>[],
  formValues: Record<string, string>
): UserVariableUpdatePayload {
  const payload: UserVariableUpdatePayload = {};

  for (const variable of variables) {
    const raw = formValues[variable.name] ?? '';

    switch (variable.type) {
      case 'boolean':
        payload[variable.name] = raw === 'true';
        break;

      case 'single_select': {
        const selected = raw.trim();
        payload[variable.name] = selected ? [selected] : variable.nullable ? null : [];
        break;
      }

      case 'list': {
        const selected = raw
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        payload[variable.name] =
          selected.length > 0 ? selected : variable.nullable ? null : [];
        break;
      }

      case 'integer':
      case 'float':
        payload[variable.name] = buildNumericPayloadValue(raw);
        break;

      case 'text':
        payload[variable.name] = raw;
        break;

      default:
        payload[variable.name] = raw;
        break;
    }
  }

  return payload;
}
